import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Avatar from "@/components/base/Avatar";
import { useToast } from "@/components/feature/ToastProvider";
import { useAuth } from "@/components/feature/AuthProvider";
import type { TreatBrief } from "@/lib/types";
import { cancelTreat, closeTreat, deleteTreat, joinTreat } from "../api";
import {
  STATE_BADGE,
  STATE_BORDER,
  STATE_STRIP,
  formatCountdown,
  isDim,
  stateLabel,
} from "../utils";

interface Props {
  brief: TreatBrief;
  /** server_ts 기준 경과 초 (PC 시계를 믿지 않는다) */
  elapsed?: number;
  /** 성공 시 그 카드만 갱신, 실패 시 목록 재조회 */
  onReload?: () => void;
}

// 목록 · 메인 공통 번개 카드
export default function TreatCard({ brief, elapsed = 0, onReload }: Props) {
  const { toast } = useToast();
  const { member, requireLogin } = useAuth();
  const [item, setItem] = useState<TreatBrief>(brief);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    setItem(brief);
    setConfirmDel(false);
  }, [brief]);

  const remaining = Math.max(0, (item.end_in ?? 0) - elapsed);
  const cd = formatCountdown(remaining);
  const unlimited = item.limit_cnt === 0;
  const left = item.left_cnt;
  const mine = item.is_mine === 1;
  const joined = item.is_joined === 1;
  const dim = isDim(item.state);
  const open = item.state === "open";
  const joins = item.joins ?? [];

  const reload = () => onReload?.();

  const becomeJoin = async () => {
    if (!member) {
      requireLogin(`/treat/${item.no}`);
      return;
    }
    setBusy(true);
    try {
      const res = await joinTreat(item.no);
      const d = res.data ?? {};
      setItem((prev) => ({
        ...prev,
        join_cnt: d.join_cnt ?? prev.join_cnt,
        left_cnt: d.left_cnt ?? prev.left_cnt,
        is_joined: 1,
        my_pick: d.pick ?? prev.my_pick,
        state: (d.left_cnt ?? prev.left_cnt) === 0 ? "full" : prev.state,
      }));
      toast(res.msg || "신청되었습니다.", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "신청에 실패했습니다.", "error");
      reload();
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const res = await cancelTreat(item.no);
      const d = res.data ?? {};
      setItem((prev) => ({
        ...prev,
        join_cnt: d.join_cnt ?? Math.max(0, prev.join_cnt - 1),
        left_cnt: d.left_cnt ?? prev.left_cnt,
        is_joined: 0,
        my_pick: 0,
        state: prev.state === "full" ? "open" : prev.state,
      }));
      toast(res.msg || "신청을 취소했습니다.", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "취소에 실패했습니다.", "error");
      reload();
    } finally {
      setBusy(false);
    }
  };

  const doClose = async () => {
    setBusy(true);
    try {
      const res = await closeTreat(item.no);
      toast(res.msg || "모집을 마감했습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "마감에 실패했습니다.", "error");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const res = await deleteTreat(item.no);
      toast(res.msg || "삭제했습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "삭제에 실패했습니다.", "error");
      setConfirmDel(false);
    } finally {
      setBusy(false);
    }
  };

  const btnBase =
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3.5 py-2 text-xs font-bold transition-colors disabled:opacity-50";

  return (
    <article
      id={`treat-${item.no}`}
      className={`relative overflow-hidden rounded-xl border bg-background-50 ${
        STATE_BORDER[item.state]
      } ${dim ? "opacity-70" : ""}`}
    >
      <span
        className={`absolute inset-y-0 left-0 w-1.5 ${STATE_STRIP[item.state]}`}
        aria-hidden="true"
      ></span>

      <div className="p-5 pl-6">
        {/* 상태 뱃지 */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATE_BADGE[item.state]}`}
          >
            {stateLabel(item.state, item.state_txt)}
          </span>
          {item.is_today === 1 && (
            <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
              오늘
            </span>
          )}
          {mine && (
            <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
              내가 쏘는 모임
            </span>
          )}
          {joined && !mine && (
            <span className="inline-block rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
              신청완료
            </span>
          )}
        </div>

        {/* 제목 */}
        <h3 className="mt-3 flex items-start gap-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <span aria-hidden="true">🍚</span>
          <Link
            to={`/treat/${item.no}`}
            className="hover:text-primary-700"
          >
            {item.subject}
          </Link>
        </h3>

        {/* 주최자 */}
        <div className="mt-2.5 flex items-center gap-2 text-sm text-foreground-600">
          <Avatar name={item.host?.name} photo={item.host?.photo} size={28} />
          <span>
            <strong className="font-semibold text-foreground-800">
              {item.host?.aho} {item.host?.name}
            </strong>{" "}
            님이 쏩니다
          </span>
        </div>

        {/* 장소 · 시각 */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-foreground-600">
          {item.place && (
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-map-pin-line text-foreground-400" aria-hidden="true"></i>
              {item.place}
            </span>
          )}
          {item.meet_time && (
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line text-foreground-400" aria-hidden="true"></i>
              {item.meet_time}
            </span>
          )}
        </div>

        {/* 카운트다운 · 인원 */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          {open && (
            <span className="inline-flex items-center gap-1.5 text-foreground-600">
              <i className="ri-alarm-warning-line text-foreground-400" aria-hidden="true"></i>
              폭파까지{" "}
              <strong
                className={`tabular-nums ${
                  cd.urgent ? "animate-pulse text-accent-700" : "text-foreground-900"
                }`}
              >
                {cd.text}
              </strong>
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-foreground-600">
            <i className="ri-group-line text-foreground-400" aria-hidden="true"></i>
            {unlimited ? (
              <>
                <strong className="text-foreground-900">{item.join_cnt}</strong>명 ·
                인원 제한 없음
              </>
            ) : (
              <>
                <strong className="text-foreground-900">{item.join_cnt}</strong> /{" "}
                {item.limit_cnt}명
              </>
            )}
          </span>
          {!unlimited && left >= 1 && left <= 2 && (
            <span className="font-bold text-accent-700">{left}자리 남음</span>
          )}
        </div>

        {/* 신청자 칩 */}
        {joins.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {joins.map((j) => (
              <span
                key={j.pick}
                className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600"
              >
                <strong className="font-heading text-foreground-400">{j.pick}</strong>
                {j.name}
              </span>
            ))}
          </div>
        )}

        {/* 액션 */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {mine ? (
            <>
              <button
                type="button"
                onClick={doClose}
                disabled={busy || item.state !== "open"}
                className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
              >
                <i className="ri-stop-circle-line" aria-hidden="true"></i>
                마감
              </button>
              {confirmDel ? (
                <>
                  <button
                    type="button"
                    onClick={doDelete}
                    disabled={busy}
                    className={`${btnBase} bg-accent-500 text-primary-950 hover:bg-accent-600`}
                  >
                    삭제 확인
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDel(false)}
                    disabled={busy}
                    className={`${btnBase} border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100`}
                  >
                    취소
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDel(true)}
                  disabled={busy}
                  className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
                >
                  <i className="ri-delete-bin-line" aria-hidden="true"></i>
                  삭제
                </button>
              )}
            </>
          ) : open ? (
            joined ? (
              <button
                type="button"
                onClick={doCancel}
                disabled={busy}
                className={`${btnBase} border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100`}
              >
                <i className="ri-close-circle-line" aria-hidden="true"></i>
                신청 취소
                {item.my_pick ? ` (내 ${item.my_pick}번째)` : ""}
              </button>
            ) : (
              <button
                type="button"
                onClick={becomeJoin}
                disabled={busy}
                className={`${btnBase} bg-primary-500 text-background-50 hover:bg-primary-600`}
              >
                <i className="ri-hand-heart-line" aria-hidden="true"></i>
                나도 갈래요
              </button>
            )
          ) : (
            <span
              className={`${btnBase} cursor-default border border-background-200 bg-background-100 text-foreground-400`}
              aria-disabled="true"
            >
              {stateLabel(item.state, item.state_txt)}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}